import React from 'react';
import { severityColours } from '../utils/nodeTemplates';
import './AggregateDecisionCard.css';

/**
 * AggregateDecisionCard
 *
 * Big "SAR_FILE / EDD_HOLD / NO_ACTION" pill plus the decision policy
 * trace so an analyst can see exactly which rule matched.
 *
 * @param {Object} props
 * @param {import('../../../types/categoricalReasoner').ViolationsReport} props.report
 * @param {string} [props.title]
 */
function AggregateDecisionCard({ report, title }) {
  if (!report) return null;
  const decision = report.aggregate_decision || report.decision || 'UNSET';
  const score = report.aggregate_score ?? report.score;
  const totalFired = report.total_packs_fired ?? 0;
  const totalLoaded = report.total_packs_loaded ?? 0;
  const totalInst = report.total_instances_fired ?? 0;
  const trace = report.aggregate_rule_trace || {};
  const rules = Array.isArray(trace.rules_evaluated) ? trace.rules_evaluated : [];

  const decisionClass = (() => {
    const d = String(decision).toUpperCase();
    if (d.includes('SAR') || d.includes('FILE')) return 'critical';
    if (d.includes('EDD') || d.includes('HOLD')) return 'medium';
    if (d.includes('NO_ACTION') || d.includes('CLEAR')) return 'low';
    return 'unknown';
  })();

  return (
    <div className="agg-decision-card">
      {title && <div className="agg-card-title">{title}</div>}
      <div className="agg-decision-row">
        <div className={`agg-decision-pill agg-${decisionClass}`}>
          {decision}
        </div>
        <div className="agg-meta">
          <div>
            <span className="agg-meta-label">Severity-weighted sum</span>
            <span className="agg-meta-value">
              {typeof score === 'number' ? score.toFixed(2) : 'n/a'}
            </span>
          </div>
          <div>
            <span className="agg-meta-label">Packs fired</span>
            <span className="agg-meta-value">{totalFired} / {totalLoaded}</span>
          </div>
          <div>
            <span className="agg-meta-label">Instances fired</span>
            <span className="agg-meta-value">{totalInst}</span>
          </div>
        </div>
      </div>

      {rules.length > 0 && (
        <div className="agg-rule-trace">
          <div className="agg-rule-trace-title">Decision policy trace</div>
          <ol>
            {rules.map((r, i) => (
              <li
                key={i}
                className={r.matched ? 'matched' : ''}
                title={r.expression || ''}
              >
                <span className="rule-expr">{r.expression || r.if || r.else || JSON.stringify(r)}</span>
                {' → '}
                <strong style={{ color: r.matched ? severityColours.HIGH : '#6b7280' }}>
                  {r.then || r.else || (r.matched ? 'matched' : 'skipped')}
                </strong>
                {r.matched && <span className="rule-badge">RULE FIRED</span>}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export default AggregateDecisionCard;
